import type {ReactElement} from 'react';
import React from 'react';
import {BaseItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-shadow-option';
import type {ItemShadowProperties} from '@PosterWhiteboard/classes/item-aura.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromAuraOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromAuraOfActiveSlideItem';
import type {RGBA} from '@Utils/color.util';

interface SlideshowItemShadowOptionProps {
  onClickColorFill(): void;
}

export function SlideshowItemShadowOption({...props}: SlideshowItemShadowOptionProps): ReactElement {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const type = useGetKeyFromAuraOfActiveSlideItem('type');
  const dropShadowBlur = useGetKeyFromAuraOfActiveSlideItem('dropShadowBlur');
  const dropShadowDistance = useGetKeyFromAuraOfActiveSlideItem('dropShadowDistance');
  const dropShadowAngle = useGetKeyFromAuraOfActiveSlideItem('dropShadowAngle');
  const glowColor = useGetKeyFromAuraOfActiveSlideItem('glowColor');
  const dropShadowColor = useGetKeyFromAuraOfActiveSlideItem('dropShadowColor');

  if (!activeItem?.isSlideshow()) {
    throw new Error(`Expected slideshow item but got ${activeItem?.gitype}`);
  }

  if (!activeSlideUID || !activeSlide) {
    throw new Error('unable to get the selected text slide');
  }

  if (!dropShadowColor || !glowColor || dropShadowAngle === undefined || dropShadowDistance === undefined || dropShadowBlur === undefined || type === undefined) {
    throw new Error('unable to get Aura Object for selected slide');
  }

  const onToggleShadow = (newProperties: ItemShadowProperties): void => {
    void activeItem.updateSlideFromObject(activeSlideUID, {
      aura: newProperties,
    });
  };

  const updateOpacity = (dropShadowAlpha: number, undoable = true): void => {
    const activeSlideDropShadowColor = dropShadowColor.slice() as RGBA;
    activeSlideDropShadowColor[3] = dropShadowAlpha;
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        aura: {
          dropShadowColor: activeSlideDropShadowColor,
        },
      },
      {
        undoable,
      }
    );
  };

  const updateAngle = (dropShadowAngle: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        aura: {
          dropShadowAngle,
        },
      },
      {
        undoable,
      }
    );
  };

  const updateDistance = (dropShadowDistance: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        aura: {
          dropShadowDistance,
        },
      },
      {
        undoable,
      }
    );
  };

  const updateBlur = (dropShadowBlur: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        aura: {
          dropShadowBlur,
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <BaseItemShadowOption
      onToggleShadow={onToggleShadow}
      onUpdateOpacity={updateOpacity}
      onUpdateAngle={updateAngle}
      onUpdateDistance={updateDistance}
      onUpdateBlur={updateBlur}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
      itemType={activeSlide.gitype}
      auraObject={{
        type,
        dropShadowColor,
        dropShadowAngle,
        dropShadowDistance,
        dropShadowBlur,
        glowColor,
      }}
    />
  );
}
